import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDownload, FiCopy, FiCheck, FiFileText, FiAlertTriangle } = FiIcons;

const ConversionResult = ({ result }) => {
  const [copied, setCopied] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(result);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const downloadJson = () => {
    const blob = new Blob([result], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'elementor-template.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const validateElementorJson = () => {
    try {
      const parsed = JSON.parse(result);
      
      // Basic Elementor JSON structure validation
      if (!parsed.title) {
        return { valid: false, message: "Missing 'title' property in Elementor JSON" };
      }
      
      if (!parsed.type) {
        return { valid: false, message: "Missing 'type' property in Elementor JSON" };
      }
      
      if (!parsed.version) {
        return { valid: false, message: "Missing 'version' property in Elementor JSON" };
      }
      
      if (!parsed.content || !Array.isArray(parsed.content)) {
        return { valid: false, message: "Missing 'content' array in Elementor JSON" };
      }
      
      // Fixed: comparing with undefined instead of empty array
      if (parsed.page_settings === undefined) {
        return { valid: false, message: "Missing 'page_settings' in Elementor JSON" };
      }
      
      // Check if content has elements
      if (parsed.content.length === 0) {
        // Empty content array is valid for some templates (like 404 pages), just warn
        return { valid: true, message: "Valid structure, but content array is empty" };
      }
      
      // Check for proper hierarchy
      let hasValidStructure = true;
      let errorDetails = "";
      
      for (const section of parsed.content) {
        if (!section.id) {
          hasValidStructure = false;
          errorDetails = "Elements must have unique 'id' property";
          break;
        }
        
        if (!section.elType) {
          hasValidStructure = false;
          errorDetails = "Elements must have 'elType' property";
          break;
        }
        
        // Fixed: comparing with undefined instead of empty array
        if (section.settings === undefined) {
          hasValidStructure = false;
          errorDetails = "Elements must have 'settings' property";
          break;
        }
        
        if (section.elements && Array.isArray(section.elements)) {
          for (const element of section.elements) {
            if (!element.id) {
              hasValidStructure = false;
              errorDetails = "All elements must have unique 'id' property";
              break;
            }
            
            if (!element.elType) {
              hasValidStructure = false;
              errorDetails = "All elements must have 'elType' property";
              break;
            }
            
            if (element.elType === 'widget' && !element.widgetType) {
              hasValidStructure = false;
              errorDetails = "Widgets must have 'widgetType' property";
              break;
            }
          }
        }
      }
      
      if (!hasValidStructure) {
        return { valid: false, message: `Invalid structure: ${errorDetails}` };
      }
      
      return { valid: true, message: "JSON appears to be valid Elementor format" };
    } catch (error) {
      return { valid: false, message: `Invalid JSON: ${error.message}` };
    }
  };

  const handleValidate = () => {
    setValidationResult(validateElementorJson());
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <SafeIcon icon={FiFileText} className="text-slate-600 text-xl" />
          <h2 className="text-xl font-semibold text-slate-800">Elementor JSON Result</h2>
        </div>
        <div className="flex items-center gap-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleValidate}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiAlertTriangle} className="text-sm" />
            Validate
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={copyToClipboard}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
          >
            <SafeIcon icon={copied ? FiCheck : FiCopy} className="text-sm" />
            {copied ? 'Copied!' : 'Copy'}
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={downloadJson}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <SafeIcon icon={FiDownload} className="text-sm" />
            Download
          </motion.button>
        </div>
      </div>

      {validationResult && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-4 p-4 rounded-lg border ${
            validationResult.valid 
              ? 'bg-green-50 border-green-200 text-green-700' 
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          <div className="flex gap-2 items-center">
            <SafeIcon 
              icon={validationResult.valid ? FiCheck : FiAlertTriangle} 
              className={`${validationResult.valid ? 'text-green-600' : 'text-red-600'} text-lg`} 
            />
            <span>{validationResult.message}</span>
          </div>
        </motion.div>
      )}

      <div className="relative">
        <pre className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm font-mono overflow-auto max-h-96">
          <code>{result}</code>
        </pre>
      </div>

      <div className="mt-4 p-4 bg-yellow-50 rounded-xl border border-yellow-200">
        <p className="text-sm text-yellow-800">
          <strong>Elementor JSON Requirements:</strong>
        </p>
        <ul className="text-sm text-yellow-700 mt-2 ml-4 list-disc space-y-1">
          <li>Must include <code>title</code> string, <code>type</code> string, <code>content</code> array, <code>page_settings</code> object, and <code>version</code> string</li>
          <li>Each element needs unique <code>id</code>, <code>elType</code>, and <code>settings</code> object</li>
          <li>Valid document types: page, header, footer, popup, error-404, etc.</li>
          <li>Widgets need <code>widgetType</code> property (e.g., "heading", "text-editor")</li>
          <li>Use objects for values with units: {JSON.stringify({"unit": "px", "size": 45})}</li>
        </ul>
      </div>

      <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-200">
        <p className="text-sm text-green-800">
          <strong>Import Instructions:</strong>
        </p>
        <ol className="text-sm text-green-700 mt-2 ml-4 list-decimal space-y-1">
          <li>Go to your WordPress admin → Elementor → Templates</li>
          <li>Click "Import Templates" button at the top</li>
          <li>Upload the downloaded JSON file</li>
          <li>The template will be available in your Elementor library</li>
          <li>If import fails, check the JSON format with the "Validate" button</li>
        </ol>
      </div>
    </motion.div>
  );
};

export default ConversionResult;